import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Carousel,
    CarouselControl,
    CarouselItem,
    Col,
    Container,
    Modal,
    ModalBody,
    Label, Input,
    ModalHeader,
    Nav,
    NavItem,
    NavLink,
    Row,
    TabContent,
    Table,
    TabPane,
} from "reactstrap";
import classnames from "classnames";
import { isEmpty } from "lodash";

//Import Star Ratings
import StarRatings from "react-star-ratings";

//Import Product Images
import { productImages } from "assets/images/product";

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";

//Import actions
import {
    getProductDetail as onGetProductDetail,
    getProductComments,
    onAddReply as onAddReplyAction,
    onAddComment as onAddCommentAction,
} from "store/actions";
import RecentProducts from "./RecentProducts";
import Reviews from "./Reviews";

//redux
import { useSelector, useDispatch } from "react-redux";

import { useParams, useSearchParams } from 'react-router-dom';
import { FaWhatsapp } from "react-icons/fa";
import { getPropertyByTitle, getMonthUpdate } from "service/api";
import Lightbox from "react-image-lightbox";
import { data } from "autoprefixer";

// --- AI Expert helpers ---
const generateAIInsights = (property) => {
    const insights = [];
    if (!property) return insights;
    const price = parseFloat(String(property.price || '').replace(/[^\d.]/g, ''));
    if (!isNaN(price)) {
        if (price < 50) insights.push("Budget-friendly price — great for first-time buyers.");
        else if (price < 200) insights.push("Mid-range property — strong resale and rental potential.");
        else insights.push("Premium pricing — strong resale and rental potential.");
    }
    if (property.status) {
        const s = property.status.toLowerCase();
        if (s.includes('ready')) insights.push("Ready to move in — zero waiting period.");
        else if (s.includes('under')) insights.push("Under construction — early buyer pricing advantage.");
        else if (s.includes('sale')) insights.push("For Sale — contact for availability.");
    }
    if (property.property_details?.[0]?.bedrooms > 0)
        insights.push(`${property.property_details[0].bedrooms} BHK layout — ideal for families.`);
    if (property.area_sqft)
        insights.push(`${property.area_sqft} sqft — spacious living area.`);
    if (property.city)
        insights.push(`Located in ${property.city}${property.location ? ', ' + property.location : ''}.`);
    if (property.festive_offer === 'yes')
        insights.push("Special festive offer — limited time deal.");
    return insights.slice(0, 4);
};

const calculateEMI = (priceStr) => {
    const price = parseFloat(String(priceStr || '').replace(/[^\d.]/g, ''));
    if (!price || price <= 0) return null;
    const principal = price * 100000 * 0.9;
    const r = 8.5 / 100 / 12;
    const n = 240;
    const emi = (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return Math.round(emi).toLocaleString('en-IN');
};

const getYTEmbedAI = (url) => {
    if (!url) return null;
    let videoID = null;
    if (url.includes('youtube.com/watch?v=')) videoID = url.split('v=')[1]?.split('&')[0];
    else if (url.includes('youtu.be/')) videoID = url.split('youtu.be/')[1]?.split('?')[0];
    else if (url.includes('youtube.com/live/')) videoID = url.split('/live/')[1]?.split('?')[0];
    else if (url.includes('youtube.com/embed/')) {
        return url.includes('autoplay=') ? url : `${url}${url.includes('?') ? '&' : '?'}autoplay=1&mute=1`;
    }
    return videoID ? `https://www.youtube.com/embed/${videoID}?autoplay=1&mute=1&rel=0&playsinline=1` : null;
};

const AIExpertModalDetail = ({ isOpen, property, onClose }) => {
    const [imgIdx, setImgIdx] = useState(0);
    const imgScrollRef = useRef(null);
    const images = property?.images || [];
    const videos = (property?.videos || []).filter(v => v && String(v).trim() !== '');

    useEffect(() => {
        if (!isOpen || images.length <= 1) return;
        const t = setInterval(() => {
            setImgIdx(prev => (prev + 1) % images.length);
        }, 3000);
        return () => clearInterval(t);
    }, [isOpen, images.length]);

    useEffect(() => {
        if (imgScrollRef.current) {
            const w = imgScrollRef.current.offsetWidth;
            imgScrollRef.current.scrollTo({ left: w * imgIdx, behavior: 'smooth' });
        }
    }, [imgIdx]);

    useEffect(() => {
        if (isOpen) setImgIdx(0);
    }, [isOpen]);

    if (!isOpen || !property) return null;
    const insights = generateAIInsights(property);
    const emi = calculateEMI(property.price);
    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)',
                zIndex: 99999, display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: '16px',
            }}
            onClick={onClose}
        >
            <style>{`
                .ai-modal-scroll::-webkit-scrollbar { display: none; }
                .ai-modal-scroll-x::-webkit-scrollbar { height: 4px; }
                .ai-modal-scroll-x::-webkit-scrollbar-thumb {
                    background: linear-gradient(90deg, #7B2FBE, #9D4EDD);
                    border-radius: 10px;
                }
            `}</style>
            <div
                style={{
                    background: 'linear-gradient(145deg, #0f0528 0%, #1e0f4a 50%, #2a1060 100%)',
                    borderRadius: '18px', maxWidth: '420px', width: '100%',
                    maxHeight: '88vh', overflowY: 'auto',
                    border: '1px solid rgba(157, 78, 221, 0.5)',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
                    scrollbarWidth: 'thin', scrollbarColor: 'rgba(157,78,221,0.3) transparent',
                }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ position: 'relative' }}>
                    {images.length > 0 ? (
                        <>
                            <div
                                ref={imgScrollRef}
                                style={{
                                    display: 'flex', overflowX: 'auto',
                                    scrollSnapType: 'x mandatory',
                                    height: '190px', width: '100%',
                                    scrollbarWidth: 'none',
                                    borderTopLeftRadius: '18px', borderTopRightRadius: '18px',
                                }}
                                className="ai-modal-scroll"
                            >
                                {images.map((img, i) => {
                                    const src = img?.url ? `https://aiinrealestate.in/api/${img.url}` : (typeof img === 'string' ? `https://aiinrealestate.in/api${img}` : '');
                                    return (
                                        <div key={i} style={{ minWidth: '100%', height: '190px', flexShrink: 0, scrollSnapAlign: 'start' }}>
                                            <img src={src} alt={`${property.title || 'Property'} ${i + 1}`}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                    );
                                })}
                            </div>
                            {images.length > 1 && (
                                <div style={{
                                    position: 'absolute', bottom: '10px', left: '50%',
                                    transform: 'translateX(-50%)', display: 'flex', gap: '5px',
                                    background: 'rgba(0,0,0,0.4)', padding: '5px 8px', borderRadius: '10px',
                                }}>
                                    {images.map((_, i) => (
                                        <div key={i} onClick={() => setImgIdx(i)} style={{
                                            width: imgIdx === i ? '16px' : '6px', height: '6px', borderRadius: '3px',
                                            background: imgIdx === i ? '#fff' : 'rgba(255,255,255,0.5)',
                                            transition: 'width 0.3s', cursor: 'pointer',
                                        }} />
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ height: '100px', background: 'linear-gradient(135deg, #2d1b69, #4a0e8f)', borderTopLeftRadius: '18px', borderTopRightRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: '2.5rem' }}>🏠</span>
                        </div>
                    )}
                    <button
                        onClick={() => {
                            const slug = property?.title?.trim().replace(/\s+/g, '-') || 'property';
                            const shareURL = `${window.location.origin}/residential/${slug}?ai=true`;
                            const msg = encodeURIComponent(`✨ AI Property Expert Analysis\n\n🏡 ${property.title || 'Property'}\n💰 ${property.price ? '₹' + property.price : 'Price on Request'}\n📍 ${[property.location, property.city].filter(Boolean).join(', ')}\n\n${shareURL}`);
                            window.open(`https://wa.me/?text=${msg}`, '_blank');
                        }}
                        style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(37,211,102,0.85)', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: '#fff', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Share on WhatsApp"
                    ><FaWhatsapp /></button>
                    <button onClick={onClose} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: '#fff', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>

                {/* Video section */}
                {videos.length > 0 && (
                    <div style={{ padding: '10px 12px 0 12px' }}>
                        <div style={{
                            display: 'flex', overflowX: 'auto', gap: '8px',
                            scrollSnapType: 'x mandatory', scrollbarWidth: 'thin',
                            scrollbarColor: 'rgba(157,78,221,0.4) transparent',
                            paddingBottom: '4px',
                        }} className="ai-modal-scroll-x">
                            {videos.map((v, i) => {
                                const embed = getYTEmbedAI(v);
                                if (!embed) return null;
                                return (
                                    <iframe
                                        key={i}
                                        src={embed}
                                        title={`video-${i}`}
                                        style={{
                                            minWidth: '100%', height: '180px', border: 'none',
                                            borderRadius: '10px', flexShrink: 0,
                                            scrollSnapAlign: 'start',
                                        }}
                                        allow="autoplay; accelerometer; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}

                <div style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div style={{ flex: 1, marginRight: '12px' }}>
                            <h6 style={{ color: '#e2d9f3', margin: 0, fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}>{property.title || 'Property'}</h6>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>{property.price ? `₹${property.price}` : 'Price on Request'}</div>
                            <div style={{ color: '#8880a0', fontSize: '0.68rem', marginTop: '2px' }}>{[property.location, property.city].filter(Boolean).join(', ')}</div>
                        </div>
                    </div>

                    <div style={{ background: 'linear-gradient(135deg, rgba(74,222,128,0.07), rgba(34,211,238,0.04))', border: '1px solid rgba(74,222,128,0.3)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'linear-gradient(135deg, #4ade80, #22d3ee)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>✨</div>
                                <span style={{ color: '#4ade80', fontWeight: 700, fontSize: '0.88rem' }}>AI Property Expert Analysis</span>
                            </div>
                            <span style={{ background: 'linear-gradient(135deg, #00C853, #00e676)', color: '#fff', fontSize: '0.6rem', fontWeight: 800, padding: '3px 8px', borderRadius: '4px' }}>SMART PICK</span>
                        </div>
                        {insights.length > 0 ? insights.map((insight, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', marginBottom: '7px' }}>
                                <span style={{ color: '#4ade80', fontSize: '0.72rem', flexShrink: 0, marginTop: '1px' }}>✓</span>
                                <span style={{ color: '#d4fce8', fontSize: '0.78rem', lineHeight: 1.45 }}>{insight}</span>
                            </div>
                        )) : (
                            <div style={{ color: '#a0c4a0', fontSize: '0.78rem' }}>AI analysis based on property data.</div>
                        )}
                    </div>

                    {emi && (
                        <div style={{ background: 'rgba(199,125,255,0.07)', border: '1px solid rgba(199,125,255,0.25)', borderRadius: '12px', padding: '13px', marginBottom: '12px' }}>
                            <div style={{ color: '#8880a0', fontSize: '0.65rem', marginBottom: '5px', letterSpacing: '0.6px', textTransform: 'uppercase' }}>EMI Starts At</div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                                <span style={{ color: '#c77dff', fontSize: '1.25rem', fontWeight: 700 }}>₹{emi}</span>
                                <span style={{ color: '#8880a0', fontSize: '0.72rem' }}>/month</span>
                                <span style={{ color: '#6b6b80', fontSize: '0.62rem', marginLeft: 'auto' }}>8.5% for 20 yrs*</span>
                            </div>
                            <div style={{ color: '#6b6b80', fontSize: '0.62rem', marginTop: '3px' }}>*Indicative — 90% loan on principal</div>
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                        {[
                            { icon: '🏠', label: 'Type', value: property.property_type || 'N/A' },
                            { icon: '📐', label: 'Area', value: property.area_sqft ? `${property.area_sqft} sqft` : 'N/A' },
                            { icon: '🛏️', label: 'Bedrooms', value: property.property_details?.[0]?.bedrooms > 0 ? `${property.property_details[0].bedrooms} BHK` : 'N/A' },
                            { icon: '📋', label: 'Status', value: property.status || 'N/A' },
                        ].map((stat, i) => (
                            <div key={i} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '9px', padding: '10px 12px', border: '1px solid rgba(255,255,255,0.07)' }}>
                                <div style={{ fontSize: '0.62rem', color: '#7a7090', marginBottom: '4px' }}>{stat.icon} {stat.label}</div>
                                <div style={{ fontSize: '0.78rem', color: '#e2d9f3', fontWeight: 600 }}>{stat.value}</div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </div>
    );
};
// --- end AI Expert helpers ---

const PropertyDetail = () => {
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 41 }, (_, i) => {
        const year = currentYear - 10 + i;
        return { value: year.toString(), label: year.toString() };
    });

    const monthOptions = [
        { value: "01", label: "January" },
        { value: "02", label: "February" },
        { value: "03", label: "March" },
        { value: "04", label: "April" },
        { value: "05", label: "May" },
        { value: "06", label: "June" },
        { value: "07", label: "July" },
        { value: "08", label: "August" },
        { value: "09", label: "September" },
        { value: "10", label: "October" },
        { value: "11", label: "November" },
        { value: "12", label: "December" }
    ];

    // meta title
    document.title = "Property Details | Aiinrealestate";

    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState("1");
    const { title } = useParams();
    const [searchParams] = useSearchParams();
    const [property, setProperty] = useState(null);
    const [aiModalOpen, setAiModalOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [propertyId, setPropertyId] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedYear, setSelectedYear] = useState("");
    const [monthUpdates, setMonthUpdates] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalImage, setModalImage] = useState("");
    const [error, setError] = useState(null);

    const [isExpanded, setIsExpanded] = useState(false);

    const toggleReadMore = () => {
        setIsExpanded(!isExpanded);
    };

    // Ensuring property description is available
    const description = property?.property?.description || "";
    // Creating a short preview of approximately 5 lines
    const maxChars = 300; // Adjust based on line length
    const isLongText = description.length > maxChars;
    const shortText = isLongText ? description.slice(0, maxChars) + "..." : description;

    const cardStyle = {
        background: "rgba(255, 255, 255, 0.1)",
        border: "none",
        borderRadius: "15px",
    };

    const phoneNumber = "+919586363303";

    // Function to generate the WhatsApp URL with the current page URL
    const generateWhatsAppLink = () => {
        const currentPageURL = window.location.href; // Get the current page URL
        const message = `Hello, I'm interested in the property listed here: ${currentPageURL}`;
        return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    };

    // const getYouTubeEmbedURL = (url) => {
    //     let videoID = null;

    //     if (url.includes("youtube.com/watch?v=")) {
    //         videoID = url.split("v=")[1]?.split("&")[0];
    //     } else if (url.includes("youtu.be/")) {
    //         videoID = url.split("youtu.be/")[1]?.split("?")[0];
    //     } else if (url.includes("youtube.com/live/")) {
    //         videoID = url.split("/live/")[1]?.split("?")[0];
    //     }
    //     return videoID ? `https://www.youtube.com/embed/${videoID}` : null;
    // };

    const getYouTubeEmbedURL = (url) => {
        let videoID = null;

        if (url.includes("youtube.com/watch?v=")) {
            videoID = url.split("v=")[1]?.split("&")[0];
        } else if (url.includes("youtu.be/")) {
            videoID = url.split("youtu.be/")[1]?.split("?")[0];
        } else if (url.includes("youtube.com/live/")) {
            videoID = url.split("/live/")[1]?.split("?")[0];
        }

        return videoID ? `https://www.youtube.com/embed/${videoID}?autoplay=1&mute=1&loop=1&controls=1` : null;
    };

    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const response = await getPropertyByTitle(title);
                if (response?.data) {
                    setProperty(response.data);
                    const id = response.data.property.id;
                    setPropertyId(id);
                    if (searchParams.get('ai') === 'true') {
                        setAiModalOpen(true);
                    }
                } else {
                    setError("Property not found.");
                }
            } catch (err) {
                setError("Error fetching property details.");
                console.error(err);
            }
        };
        fetchProperty();
    }, [title]);

    const fetchMonthUpdates = async (selectedYear, month) => {
        try {
            const response = await getMonthUpdate(propertyId, selectedYear, month);

            if (response?.data?.success) {
                console.log("Month updates fetched:", response.data.data);
                setMonthUpdates(response.data.data);
            } else {
                console.log("No month updates found for this property");
                setError(response?.data?.message || "No month updates found for this property.");
                setMonthUpdates([]);
            }
        } catch (err) {
            setError("Error fetching month updates.");
            console.error(err);
        }
    };




    // const images = property?.property?.images?.map(img => `http://localhost:5000/api${img}`) || [];
    // const images = property?.property?.images?.map(img => `http://localhost:5000${img.url}`) || [];

    const images = property?.property?.images?.map(img => `https://aiinrealestate.in/api/${img.url}`) || [];
    const visibleImages = images.slice(0, 3);
    const extraImagesCount = images.length - visibleImages.length;
    const videos = property?.property?.videos || [];
    const filteredVideos = videos.filter(video => video && video.trim() !== "");

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {property && (
                        <Row>
                            <Col>
                                <Card style={cardStyle}>
                                    <CardBody style={{ backgroundColor: '#1E253A', borderRadius: '10px', padding: '20px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
                                        <Row>
                                            <Col xl={6}>
                                                <div className="product-detai-imgs">
                                                    <Row>
                                                        <Col md={2} sm={3} className="col-4">
                                                            {visibleImages.map((imgSrc, index) => (
                                                                <img
                                                                    key={index}
                                                                    src={imgSrc}
                                                                    alt={`Thumbnail ${index}`}
                                                                    className="img-fluid mb-2 rounded"
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={() => {
                                                                        setPhotoIndex(index);
                                                                        setIsOpen(true);
                                                                    }}
                                                                />
                                                            ))}
                                                            {extraImagesCount > 0 && (
                                                                <Button
                                                                    color="primary"
                                                                    className="mt-0"
                                                                    onClick={() => setIsOpen(true)}
                                                                >
                                                                    +{extraImagesCount} More
                                                                </Button>
                                                            )}
                                                        </Col>

                                                        <Col md={7} sm={9} className="offset-md-1 col-8">
                                                            <img
                                                                src={images[photoIndex]}
                                                                alt="Property"
                                                                className="img-fluid rounded"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => setIsOpen(true)}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    {filteredVideos.length > 0 ? (
                                                        <div style={{ marginTop: "20px", textAlign: "center" }}>
                                                            <Carousel
                                                                activeIndex={activeIndex}
                                                                next={() => setActiveIndex((activeIndex + 1) % filteredVideos.length)}
                                                                previous={() => setActiveIndex((activeIndex + filteredVideos.length - 1) % filteredVideos.length)}
                                                            >
                                                                {filteredVideos.map((video, index) => (
                                                                    <CarouselItem key={index}>
                                                                        <iframe
                                                                            width="100%"
                                                                            height="315"
                                                                            src={getYouTubeEmbedURL(video)}
                                                                            title={`Video ${index}`}
                                                                            frameBorder="0"
                                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                            allowFullScreen
                                                                            style={{
                                                                                borderRadius: "10px",
                                                                                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                                                                                maxWidth: "100%",
                                                                            }}
                                                                        ></iframe>
                                                                    </CarouselItem>
                                                                ))}

                                                                {filteredVideos.length > 1 && (
                                                                    <>
                                                                        <CarouselControl
                                                                            direction="prev"
                                                                            directionText="Previous"
                                                                            onClickHandler={() => setActiveIndex((activeIndex + filteredVideos.length - 1) % filteredVideos.length)}
                                                                        />
                                                                        <CarouselControl
                                                                            direction="next"
                                                                            directionText="Next"
                                                                            onClickHandler={() => setActiveIndex((activeIndex + 1) % filteredVideos.length)}
                                                                        />
                                                                    </>
                                                                )}
                                                            </Carousel>
                                                        </div>
                                                    ) : (
                                                        <div style={{
                                                            backgroundColor: '#2C3E50',
                                                            borderRadius: '8px',
                                                            padding: '20px',
                                                            marginTop: '80px',
                                                            textAlign: "center"
                                                        }}>
                                                            <p className="text-muted"
                                                                style={{
                                                                    fontSize: '1rem',
                                                                    color: '#D3D3D3',
                                                                    lineHeight: '1.5',
                                                                    whiteSpace: 'pre-line',
                                                                }}
                                                            >
                                                                {isExpanded ? description : shortText}
                                                            </p>
                                                            {isLongText && (
                                                                <button
                                                                    onClick={toggleReadMore}
                                                                    style={{
                                                                        background: 'none',
                                                                        border: 'none',
                                                                        color: '#A3C4FF',
                                                                        cursor: 'pointer',
                                                                        marginTop: '5px',
                                                                        fontSize: '0.9rem',
                                                                        textDecoration: 'underline'
                                                                    }}
                                                                >
                                                                    {isExpanded ? "Read Less" : "Read More"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}



                                                </div>
                                            </Col>

                                            <Col xl={6}>
                                                <div className="mt-4 mt-xl-3" style={{ color: '#F1F1F1' }}>
                                                    <Link to="#" className="text-primary" style={{ fontWeight: 'bold' }}>
                                                        {property?.property?.title}, {property?.property?.location}
                                                    </Link>

                                                    <h4 className="mt-1 mb-3 text-light" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                                                        {property?.property?.title}
                                                        <a
                                                            href={generateWhatsAppLink()}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: "#25D366", fontSize: "1.8rem", marginLeft: "10px" }}
                                                            title="Chat on WhatsApp"
                                                        >
                                                            <FaWhatsapp />
                                                        </a>
                                                        <Link to="/Emi-Calculator" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                className="btn"
                                                                style={{
                                                                    background: "linear-gradient(135deg, #FFD700, #FFA500)",
                                                                    color: "#1C142C",
                                                                    fontWeight: "600",
                                                                    border: "none",
                                                                    borderRadius: "8px",
                                                                    padding: "4px 12px",
                                                                    fontSize: "0.9rem",
                                                                    marginLeft: "15px",
                                                                    transform: "translateY(-2px)",
                                                                }}
                                                            >
                                                                EMI
                                                            </button>

                                                        </Link>

                                                        {property?.property?.status === "Under Construction" && (
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    gap: "20px",
                                                                    marginBottom: "20px",
                                                                }}
                                                            >
                                                                <Input
                                                                    id="yearSelect"
                                                                    type="select"
                                                                    value={selectedYear}
                                                                    onChange={(e) => setSelectedYear(e.target.value)}
                                                                    style={{
                                                                        background: "linear-gradient(135deg, #FFD700, #FFA500)",
                                                                        color: "#1C142C",
                                                                        fontWeight: "600",
                                                                        border: "none",
                                                                        borderRadius: "8px",
                                                                        padding: "6px 12px",
                                                                        fontSize: "0.9rem",
                                                                        width: "48%",
                                                                        appearance: "none",
                                                                        WebkitAppearance: "none",
                                                                        MozAppearance: "none",
                                                                    }}
                                                                >
                                                                    <option value="" disabled style={{ color: "#999" }}>
                                                                        Select Year
                                                                    </option>
                                                                    {yearOptions.map((year) => (
                                                                        <option key={year.value} value={year.value}>
                                                                            {year.label}
                                                                        </option>
                                                                    ))}
                                                                </Input>

                                                                <Input
                                                                    id="monthSelect"
                                                                    type="select"
                                                                    value={selectedMonth}
                                                                    onChange={(e) => {
                                                                        const month = e.target.value;
                                                                        setSelectedMonth(month);
                                                                        if (propertyId && month && selectedYear) {
                                                                            fetchMonthUpdates(selectedYear, month);
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        background: "linear-gradient(135deg, #FFD700, #FFA500)",
                                                                        color: "#1C142C",
                                                                        fontWeight: "600",
                                                                        border: "none",
                                                                        borderRadius: "8px",
                                                                        padding: "6px 12px",
                                                                        fontSize: "0.9rem",
                                                                        width: "48%",
                                                                        appearance: "none",
                                                                        WebkitAppearance: "none",
                                                                        MozAppearance: "none",
                                                                    }}
                                                                >
                                                                    <option value="" disabled style={{ color: "#999" }}>
                                                                        Monthly Property Progress
                                                                    </option>
                                                                    {monthOptions.map((month) => (
                                                                        <option key={month.value} value={month.value}>
                                                                            {month.label}
                                                                        </option>
                                                                    ))}
                                                                </Input>
                                                            </div>
                                                        )}



                                                    </h4>
                                                    {/* <p className="text-muted mb-4"> Super built-up Area :4.9K sq.m. </p> */}

                                                    {/* Price Section */}
                                                    <Row>
                                                        {property?.property?.property_details?.map((detail, index) => (
                                                            <Col xl={6} key={index}>
                                                                <div style={{
                                                                    backgroundColor: '#34495E',
                                                                    borderRadius: '8px',
                                                                    padding: '15px',
                                                                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                                                                    marginBottom: '15px',
                                                                }}>
                                                                    <h4 className="text-light" style={{
                                                                        fontSize: '1.3rem',
                                                                        fontWeight: 'bold',
                                                                        color: '#ECF0F1'
                                                                    }}>
                                                                        {detail.bedrooms} BHK {property?.property?.property_type}
                                                                    </h4>
                                                                    <p className="text-muted" style={{
                                                                        fontSize: '0.9rem',
                                                                        color: '#BDC3C7'
                                                                    }}>
                                                                        Built-up Area: {detail.builtArea} sq.ft.
                                                                    </p>
                                                                    <h5 className="text-light" style={{
                                                                        fontSize: '1.2rem',
                                                                        fontWeight: 'bold',
                                                                        color: '#F39C12'
                                                                    }}>
                                                                        ₹{detail.price}
                                                                    </h5>
                                                                </div>
                                                            </Col>
                                                        ))}
                                                    </Row>


                                                    {filteredVideos.length > 0 && (
                                                        <div style={{
                                                            backgroundColor: '#2C3E50',
                                                            borderRadius: '8px',
                                                            padding: '20px',
                                                            marginBottom: '20px',
                                                        }}>
                                                            <p className="text-muted"
                                                                style={{
                                                                    fontSize: '1rem',
                                                                    color: '#D3D3D3',
                                                                    lineHeight: '1.5',
                                                                    whiteSpace: 'pre-line',
                                                                }}
                                                            >
                                                                {isExpanded ? description : shortText}
                                                            </p>
                                                            {isLongText && (
                                                                <button
                                                                    onClick={toggleReadMore}
                                                                    style={{
                                                                        background: 'none',
                                                                        border: 'none',
                                                                        color: '#A3C4FF',
                                                                        cursor: 'pointer',
                                                                        marginTop: '5px',
                                                                        fontSize: '0.9rem',
                                                                        textDecoration: 'underline'
                                                                    }}
                                                                >
                                                                    {isExpanded ? "Read Less" : "Read More"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}


                                                    {/* Property Features */}
                                                    <div style={{
                                                        backgroundColor: '#2C3E50',
                                                        borderRadius: '8px',
                                                        padding: '15px',
                                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                                        marginBottom: '20px'
                                                    }}>
                                                        <h5 style={{
                                                            color: '#ECF0F1',
                                                            fontWeight: 'bold',
                                                            marginBottom: '15px',
                                                            fontSize: '1.2rem',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '1px',
                                                        }}>Property Features</h5>

                                                        <Row>
                                                            <Col md="6">
                                                                <div style={{
                                                                    backgroundColor: '#34495E',
                                                                    borderRadius: '8px',
                                                                    padding: '15px',
                                                                    marginBottom: '15px',
                                                                }}>
                                                                    <p style={{
                                                                        fontSize: '1rem',
                                                                        color: '#ECF0F1',
                                                                        marginBottom: '8px',
                                                                    }}><strong>Property Type:</strong> {property?.property?.property_type}</p>
                                                                    <p style={{
                                                                        fontSize: '1rem',
                                                                        color: '#ECF0F1',
                                                                        marginBottom: '8px',
                                                                    }}><strong>Size:</strong> {property?.property?.area_sqft} sq.ft.</p>
                                                                    <p style={{
                                                                        fontSize: '1rem',
                                                                        color: '#ECF0F1',
                                                                        marginBottom: '8px',
                                                                    }}><strong>Price:</strong> {property?.property?.price}</p>
                                                                </div>
                                                            </Col>
                                                            <Col md="6">
                                                                <div style={{
                                                                    backgroundColor: '#34495E',
                                                                    borderRadius: '8px',
                                                                    padding: '15px',
                                                                    marginBottom: '15px',
                                                                }}>
                                                                    <p style={{
                                                                        fontSize: '1rem',
                                                                        color: '#ECF0F1',
                                                                        marginBottom: '8px',
                                                                    }}><strong>Location:</strong> {property?.property?.location}, {property?.property?.city}</p>
                                                                    <p style={{
                                                                        fontSize: '1rem',
                                                                        color: '#ECF0F1',
                                                                        marginBottom: '8px',
                                                                    }}><strong>{property?.property?.status}</strong></p>
                                                                    <p style={{
                                                                        fontSize: '1rem',
                                                                        color: '#ECF0F1',
                                                                        marginBottom: '8px',
                                                                    }}></p>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>

                                        {property?.property?.facilities?.length > 0 && (
                                            <div className="mt-4">
                                                <h5 className="text-light mb-3">Top Facilities</h5>
                                                {Array.from({ length: Math.ceil(property.property.facilities.length / 4) }, (_, rowIndex) => (
                                                    <Row key={rowIndex}>
                                                        {property.property.facilities
                                                            .slice(rowIndex * 4, rowIndex * 4 + 4)
                                                            .map((facility, colIndex) => (
                                                                <Col md={3} key={colIndex}>
                                                                    <ul className="list-unstyled text-light">
                                                                        <li className="mb-2">
                                                                            <i className="mdi mdi-check-circle text-success me-2" />
                                                                            {facility}
                                                                        </li>
                                                                    </ul>
                                                                </Col>
                                                            ))}
                                                    </Row>
                                                ))}
                                            </div>
                                        )}
                                        {property?.property?.specifications?.length > 0 && (
                                            <div className="mt-5">
                                                <h5
                                                    className="mb-3"
                                                    style={{
                                                        color: '#F1F1F1',
                                                        fontWeight: 'bold',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                    }}
                                                >
                                                    Specifications:
                                                </h5>
                                                <div className="table-responsive">
                                                    <table
                                                        className="table table-bordered"
                                                        style={{
                                                            backgroundColor: '#34495E',
                                                            color: '#D3D3D3',
                                                            borderRadius: '8px',
                                                        }}
                                                    >
                                                        <thead>
                                                            <tr>
                                                                <th style={{ color: '#F1F1F1', fontWeight: 'bold' }}>Category</th>
                                                                <th style={{ color: '#F1F1F1', fontWeight: 'bold' }}>Details</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {property.property.specifications.map((spec, index) => (
                                                                <tr key={index}>
                                                                    <td>{spec.category}</td>
                                                                    <td>{spec.detail}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                    </CardBody>
                                    <div
                                        style={{
                                            position: "fixed",
                                            bottom: "20px",
                                            right: "20px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            zIndex: 1000,
                                        }}
                                    >
                                        {/* Icons Container */}
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                marginBottom: "12px",
                                                gap: "10px", // Reduced gap for a more compact design
                                            }}
                                        >
                                            {/* WhatsApp Icon */}
                                            <a
                                                href={generateWhatsAppLink()}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: "white",
                                                    fontSize: "1.2rem", // Reduced font size
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    width: "40px", // Reduced icon size
                                                    height: "40px",
                                                    background: "linear-gradient(135deg, #1FAF51, #25D366)", // Slightly adjusted green gradient
                                                    borderRadius: "50%",
                                                    boxShadow: "0px 3px 10px rgba(31, 175, 81, 0.6)",
                                                    transition: "all 0.3s ease",
                                                }}
                                                title="Chat on WhatsApp"
                                                onMouseEnter={(e) => {
                                                    e.target.style.transform = "scale(1.1)";
                                                    e.target.style.boxShadow = "0px 5px 15px rgba(31, 175, 81, 0.8)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.transform = "scale(1)";
                                                    e.target.style.boxShadow = "0px 3px 10px rgba(31, 175, 81, 0.6)";
                                                }}
                                            >
                                                <FaWhatsapp />
                                            </a>

                                            {/* Call Icon */}
                                            <a
                                                href="tel:+919586363303"
                                                style={{
                                                    color: "white",
                                                    fontSize: "1.2rem", // Reduced font size
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    width: "40px", // Reduced icon size
                                                    height: "40px",
                                                    background: "linear-gradient(135deg, #1E90FF, #34B7F1)", // Slightly adjusted blue gradient
                                                    borderRadius: "50%",
                                                    boxShadow: "0px 3px 10px rgba(30, 144, 255, 0.6)",
                                                    transition: "all 0.3s ease",
                                                }}
                                                title="Call Us"
                                                onMouseEnter={(e) => {
                                                    e.target.style.transform = "scale(1.1)";
                                                    e.target.style.boxShadow = "0px 5px 15px rgba(30, 144, 255, 0.8)";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.transform = "scale(1)";
                                                    e.target.style.boxShadow = "0px 3px 10px rgba(30, 144, 255, 0.6)";
                                                }}
                                            >
                                                <i className="fas fa-phone" />
                                            </a>
                                        </div>

                                        {/* Query Button */}
                                        <Link
                                            to="/dashboard"
                                            title="Any Questions or Queries?"
                                            style={{
                                                background: "linear-gradient(135deg, #6a11cb, #2575fc)", // Gradient effect for the button
                                                border: "none",
                                                borderRadius: "9999px",
                                                padding: "10px 20px", // Adjusted padding
                                                fontSize: "14px", // Reduced font size
                                                color: "white",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
                                                transition: "all 0.3s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = "scale(1.1)";
                                                e.target.style.boxShadow = "0px 6px 18px rgba(0, 0, 0, 0.4)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = "scale(1)";
                                                e.target.style.boxShadow = "0px 4px 12px rgba(0, 0, 0, 0.3)";
                                            }}
                                        >
                                            Ask Ai &nbsp; <i className="fas fa-question" style={{ margin: 0 }}></i>
                                        </Link>
                                    </div>

                                </Card>
                            </Col>
                        </Row>
                    )}
                    <Row className="gy-4">
                        {monthUpdates.map((update, index) => (
                            <Col md="4" sm="6" xs="12" key={index}>
                                <Card className="shadow-lg h-100 border-0">
                                    <CardBody className="p-0 bg-dark text-white rounded-4 overflow-hidden">
                                        {/* Media Section */}
                                        {update.url ? (
                                            <div style={{ height: "200px", overflow: "hidden", backgroundColor: "#000" }}>
                                                {update.url.includes('youtube.com') || update.url.includes('youtu.be') ? (
                                                    <iframe
                                                        src={getYouTubeEmbedURL(update.url)}
                                                        style={{ width: '100%', height: '100%', border: 'none' }}
                                                        allow="autoplay; encrypted-media"
                                                        allowFullScreen
                                                    ></iframe>
                                                ) : (
                                                    <video
                                                        controls
                                                        autoPlay
                                                        muted
                                                        loop
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    >
                                                        <source src={update.url} type="video/mp4" />
                                                        Your browser does not support the video tag.
                                                    </video>
                                                )}
                                            </div>
                                        ) : update.imageUrl ? (
                                            <div style={{ height: "200px", overflow: "hidden" }}>
                                                <img
                                                    src={update.imageUrl.replace("uploads", "api/uploads")}
                                                    alt="Update"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="d-flex justify-content-center align-items-center" style={{ height: "200px", backgroundColor: "#333" }}>
                                                <i className="far fa-image fa-2x text-muted"></i>
                                            </div>
                                        )}

                                        {/* Text Info */}
                                        <div className="p-3">
                                            <p className="mb-2" style={{ fontSize: "14px" }}>
                                                {update.description || "No description available"}
                                            </p>
                                            <div className="text-muted small d-flex align-items-center">
                                                <i className="far fa-calendar-alt me-2" />
                                                {new Date(update.created_at).toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric"
                                                })}
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>


                    {isOpen && images.length > 0 && (
                        <Lightbox
                            mainSrc={images[photoIndex]}
                            nextSrc={images[(photoIndex + 1) % images.length]}
                            prevSrc={images[(photoIndex + images.length - 1) % images.length]}
                            onCloseRequest={() => setIsOpen(false)}
                            onMovePrevRequest={() =>
                                setPhotoIndex((photoIndex + images.length - 1) % images.length)
                            }
                            onMoveNextRequest={() =>
                                setPhotoIndex((photoIndex + 1) % images.length)
                            }
                            imageCaption={
                                <div style={{
                                    marginTop: '20px',
                                    fontSize: '16px',
                                    color: '#fff',
                                    textAlign: 'center',
                                    paddingBottom: '20px'
                                }}>
                                    {property?.property?.images?.[photoIndex]?.title || ""}
                                </div>
                            }
                        />
                    )}
                </Container>
            </div>
            <AIExpertModalDetail
                isOpen={aiModalOpen}
                property={property?.property || null}
                onClose={() => setAiModalOpen(false)}
            />
        </React.Fragment >
    );
}
PropertyDetail.propTypes = {
    product: PropTypes.object,
    match: PropTypes.any,
    onGetProductDetail: PropTypes.func,
};

export default withRouter(PropertyDetail);
